import { PeriodIdentifier } from '@/constants/period.constant';
import { BusinessIntelligenceContextualPeriodCode } from '@/interfaces/BusinessIntelligence.interface';
import { DashboardingProvider } from '@/providers/dashboarding.provider';
import { PrettyFeelSharedUiColorName, type PaletteVariant } from '@/shared/ui/theming';
import { deepCopy } from '@/utils/toolkit/object/copy.utils';
import type { Branded } from '@/utils/typing/branded.type';
import { isNotNullish, isNullish } from '@/utils/typing/is-nullish.predicate';
import type { LiteralValues } from '@/utils/typing/literal-values.type';
import { computed, inject, Injectable, type Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { map, of, type Observable } from 'rxjs';
import type { BusinessIndicator, BusinessIndicatorId } from '../../../interfaces/BusinessIndicator.interface';
import type { BusinessIndicatorForYearResponse } from '../details/services/advizeo-core-business-indicators.service';

const BandCode = {
    objective: 'objective',
    target: 'target',
    reference: 'reference',
    lastBand: 'lastBand',
    custom: 'custom'
} as const;
export type BandCode = LiteralValues<typeof BandCode>;

export type BandBoundaryId = Branded<string, 'BandBoundId'>;

function generateBandBoundaryId(boundCode: BandCode, index?: number | null): BandBoundaryId {
    return (isNotNullish(index) ? boundCode + index : boundCode) as BandBoundaryId;
}

export type BusinessIndicatorGaugeConfig = {
    businessIndicatorId: BusinessIndicatorId;
    year: BusinessIntelligenceContextualPeriodCode | number | null; // year means "period"
    isEstimated: boolean;
    bandBoundary:
        | { isCustomBand: false; bandBoundaryColors: BandBoundaryColor[] }
        | { isCustomBand: true; bandBoundaryColors: CustomBand[] };
    gaugeName: string;
    displayLegend: boolean;
};

export type BusinessIndicatorAdvancementConfig = {
    businessIndicatorId: BusinessIndicatorId;
    isEstimated: boolean;
    displayLegend: boolean;
    title: string;
};

export interface BandBoundaryColor {
    bandBoundaryId: BandBoundaryId;
    code: BandCode;
    color: { paletteName: PrettyFeelSharedUiColorName; variant: PaletteVariant };
}

export interface CustomBand {
    name: string;
    minThreshold: number;
    color: { paletteName: PrettyFeelSharedUiColorName; variant: PaletteVariant };
}

export interface BandBoundaryValue {
    bandBoundaryId: BandBoundaryId;
    code: BandCode;
    name?: string;
    to: number;
    from: number;
    year?: number;
}

// Maximum 3 objectives are displayed, in BFF in the future ?
const MAX_OBJECTIVE_COUNT = 3;

export interface PeriodOption {
    periodValue: number | BusinessIntelligenceContextualPeriodCode;
    periodType: PeriodType;
}

export enum PeriodType {
    YEAR = 'YEAR',
    CONTEXTUAL = 'CONTEXTUAL'
}

@Injectable({
    providedIn: 'root'
})
export class WidgetsBusinessIndicatorsService {
    private _translateService = inject(TranslateService);
    private _dashboardingProvider = inject(DashboardingProvider);

    private _selectedPeriodIdentifier = toSignal(this._dashboardingProvider.getSelectedPeriodIdentifier$());
    private _periodsYearList = toSignal(this._dashboardingProvider.getPeriodsIdentifierWithoutCurrentYear$(), { initialValue: [] });

    getBandBoundaryColors(businessIndicator: BusinessIndicator): BandBoundaryColor[] {
        const sortedDescObjectiveYears = deepCopy(businessIndicator.deadlineYears);
        sortedDescObjectiveYears.sort().reverse();

        const objectiveBandBounds: BandBoundaryColor[] = sortedDescObjectiveYears.slice(0, MAX_OBJECTIVE_COUNT).map((_year, index) => {
            return {
                bandBoundaryId: generateBandBoundaryId(BandCode.objective, index),
                code: BandCode.objective,
                color:
                    index === 0
                        ? { paletteName: PrettyFeelSharedUiColorName.GRASS, variant: '9' }
                        : index === 1
                          ? { paletteName: PrettyFeelSharedUiColorName.GRASS, variant: '5' }
                          : { paletteName: PrettyFeelSharedUiColorName.AMBER, variant: '5' }
            };
        });

        return [
            ...objectiveBandBounds,
            {
                bandBoundaryId: generateBandBoundaryId(BandCode.target),
                code: BandCode.target,
                color: { paletteName: PrettyFeelSharedUiColorName.AMBER, variant: '9' }
            },
            {
                bandBoundaryId: generateBandBoundaryId(BandCode.reference),
                code: BandCode.reference,
                color: { paletteName: PrettyFeelSharedUiColorName.ORANGE, variant: '9' }
            },
            {
                bandBoundaryId: generateBandBoundaryId(BandCode.lastBand),
                code: BandCode.lastBand,
                color: { paletteName: PrettyFeelSharedUiColorName.TOMATO, variant: '9' }
            }
        ];
    }

    getDefaultBandConfig(): CustomBand[] {
        return [
            {
                name: `${this._translateService.instant('TAILORED_WIDGET.INDICATOR_GAUGE.THRESHOLD')} 1`,
                minThreshold: 0,
                color: { paletteName: PrettyFeelSharedUiColorName.GRASS, variant: '9' }
            },
            {
                name: `${this._translateService.instant('TAILORED_WIDGET.INDICATOR_GAUGE.THRESHOLD')} 2`,
                minThreshold: 100,
                color: { paletteName: PrettyFeelSharedUiColorName.GRASS, variant: '5' }
            },
            {
                name: `${this._translateService.instant('TAILORED_WIDGET.INDICATOR_GAUGE.THRESHOLD')} 3`,
                minThreshold: 200,
                color: { paletteName: PrettyFeelSharedUiColorName.AMBER, variant: '5' }
            }
        ];
    }

    getBandBoundaryValues(
        businessIndicatorForYear: BusinessIndicatorForYearResponse,
        requestedYear: number,
        businessIndicatorValue: number
    ): {
        min: number;
        max: number;
        bandBoundaryValues: BandBoundaryValue[];
    } | null {
        if (isNullish(businessIndicatorForYear.reference) || isNullish(businessIndicatorForYear.target)) return null;

        const sortedDescObjectives = businessIndicatorForYear.objectives.sort(
            (objectiveA, objectiveB) => objectiveB.year - objectiveA.year
        );

        let min;

        if (sortedDescObjectives.length > 0) {
            const firstObjectiveMinus10Percent = sortedDescObjectives[0].value * 0.9;
            min = Math.min(businessIndicatorValue, firstObjectiveMinus10Percent);
        } else {
            min = 0;
        }

        const referencePlus10Percent = businessIndicatorForYear.reference * 1.1;
        const max = Math.max(businessIndicatorValue, referencePlus10Percent);

        const bandBoundaryValuesFromSortedObjectives = sortedDescObjectives
            .slice(0, MAX_OBJECTIVE_COUNT)
            .map((objective, index, { length }) => {
                // last value
                if (index + 1 === length) {
                    return {
                        bandBoundaryId: generateBandBoundaryId(BandCode.target),
                        code: BandCode.target,
                        from: objective.value,
                        to: businessIndicatorForYear.target ?? 0,
                        year: requestedYear
                    };
                } else {
                    return {
                        bandBoundaryId: generateBandBoundaryId(BandCode.objective, index + 1),
                        code: BandCode.objective,
                        from: objective.value,
                        to: sortedDescObjectives[index + 1].value ?? 0,
                        year: sortedDescObjectives[index + 1].year
                    };
                }
            });

        const bandBoundaryValues: BandBoundaryValue[] = [
            ...(sortedDescObjectives.length > 0
                ? [
                      {
                          bandBoundaryId: generateBandBoundaryId(BandCode.objective, 0),
                          code: BandCode.objective,
                          from: min,
                          to: sortedDescObjectives[0].value,
                          year: sortedDescObjectives[0].year
                      },
                      ...bandBoundaryValuesFromSortedObjectives,
                      {
                          bandBoundaryId: generateBandBoundaryId(BandCode.reference),
                          code: BandCode.reference,
                          from: businessIndicatorForYear.target,
                          to: businessIndicatorForYear.reference
                      }
                  ]
                : [
                      {
                          bandBoundaryId: generateBandBoundaryId(BandCode.reference),
                          code: BandCode.reference,
                          from: min,
                          to: businessIndicatorForYear.reference
                      }
                  ]),
            {
                bandBoundaryId: generateBandBoundaryId(BandCode.lastBand),
                code: BandCode.lastBand,
                from: businessIndicatorForYear.reference,
                to: max
            }
        ];

        return {
            min,
            max,
            bandBoundaryValues
        };
    }

    getBandBoundaryValuesFromCustom(
        customBands: CustomBand[],
        businessIndicatorValue: number
    ): {
        min: number;
        max: number;
        bandBoundaryValues: BandBoundaryValue[];
    } | null {
        const length = customBands.length;
        if (length === 0) {
            return null;
        }

        const interval = customBands[length - 1].minThreshold - (customBands.at(length - 2)?.minThreshold ?? 0);

        const min = Math.min(businessIndicatorValue, customBands[0].minThreshold);
        const max = Math.max(businessIndicatorValue, customBands[length - 1].minThreshold + interval);

        const bandBoundaryValues: BandBoundaryValue[] = [];

        for (let i = 0; i < customBands.length; i++) {
            bandBoundaryValues.push({
                bandBoundaryId: generateBandBoundaryId('custom', i),
                code: 'custom',
                from: customBands[i].minThreshold,
                to: customBands.at(i + 1)?.minThreshold ?? max,
                name: customBands[i].name
            });
        }

        return {
            min,
            max,
            bandBoundaryValues
        };
    }

    getBusinessIndicatorValue(measured: number | null, estimated: number | null, isEstimated: boolean): number | null {
        if (isNullish(measured) || (isEstimated && isNullish(estimated))) return null;
        return isEstimated ? measured + (estimated ?? 0) : measured;
    }

    getComputedYear$(config: BusinessIndicatorGaugeConfig): Observable<number | null> {
        if (config.year === BusinessIntelligenceContextualPeriodCode.CURRENT_PERIOD) {
            return this._dashboardingProvider
                .getSelectedPeriodIdentifier$()
                .pipe(map(periodIdentifier => (periodIdentifier === PeriodIdentifier.LAST_TWELVE_MONTHS ? null : periodIdentifier)));
        }

        if (typeof config.year === 'number') {
            return of(config.year);
        }

        throw new Error('unsupported period type in widget business indicator gauge');
    }

    getComputedYear(year: BusinessIntelligenceContextualPeriodCode | number | null): number | null {
        if (year === BusinessIntelligenceContextualPeriodCode.CURRENT_PERIOD) {
            const periodIdentifier = this._selectedPeriodIdentifier();
            return periodIdentifier === PeriodIdentifier.LAST_TWELVE_MONTHS || isNullish(periodIdentifier) ? null : periodIdentifier;
        }

        if (typeof year === 'number') {
            return year;
        }

        return null;
    }

    getPeriodList(): Signal<PeriodOption[]> {
        return computed(() => [
            ...this._periodsYearList().map(year => ({ periodValue: year, periodType: PeriodType.YEAR })),
            { periodValue: BusinessIntelligenceContextualPeriodCode.CURRENT_PERIOD, periodType: PeriodType.CONTEXTUAL }
        ]);
    }
}
